import { useEffect } from 'react';

/**
 * Keep the composer above the on-screen keyboard.
 *
 * iOS Safari does not shrink the layout viewport when the keyboard opens; it
 * shrinks the *visual* viewport and lets the page scroll under it. A footer
 * pinned to the layout bottom therefore ends up behind the keys. The gap
 * between the two viewports is published as --kb so the footer can lift by
 * exactly that much. Android Chrome resizes the layout instead (see the
 * viewport meta), where this stays at zero.
 */
export function useKeyboardInset(): void {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return;
    const root = document.documentElement;
    const apply = (): void => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      root.style.setProperty('--kb', `${Math.round(inset)}px`);
    };
    apply();
    viewport.addEventListener('resize', apply);
    viewport.addEventListener('scroll', apply);
    return () => {
      viewport.removeEventListener('resize', apply);
      viewport.removeEventListener('scroll', apply);
      root.style.removeProperty('--kb');
    };
  }, []);
}
